/**
 * SIM status → pill tone — ARCHITECTURE §16.4.
 *
 * **Hand-written from the values actually observed on the wire.** §13.8 records
 * that the vendor never documents the spelling of a SIM status, so this map
 * cannot be exhaustive and does not pretend to be: an unrecognised status
 * renders with its **raw label** and a neutral tone rather than being hidden or
 * guessed at. A status we have never seen is information, not an error.
 *
 * The tones are the platform semantic palette. A pill never uses the brand
 * colour, so it still reads correctly for a tenant whose primary is red or
 * green — and every pill carries a text label, because colour is never the only
 * channel (WCAG 1.4.1).
 */

import type { BadgeTone } from '../../components/Badge.js';

import { normaliseStatus } from './actions/actionRegistry.js';

/**
 * Observed spellings, normalised (upper case, separators collapsed to `_`).
 * Written out one per line so adding one is a deliberate edit with a tone
 * decision attached, not a regex someone widens.
 */
const TONE_BY_STATUS: Record<string, BadgeTone> = {
  ACTIVE: 'success',
  AVAILABLE: 'neutral',
  /*
    The live upstream calls an un-activated SIM `INITIAL`, not
    `AVAILABLE` — confirmed against the real account on 2026-09-03, where a
    basket of 5,000 reported 3,391 ACTIVE and exactly 1,609 INITIAL, matching
    its available count. It is the same state, so it takes the same tone.
  */
  INITIAL: 'neutral',
  INACTIVE: 'neutral',
  TEST_MODE: 'progress',
  TESTMODE: 'progress',
  IN_PROGRESS: 'progress',
  INPROGRESS: 'progress',
  SAFE_CUSTODY: 'warning',
  SAFECUSTODY: 'warning',
  SUSPENDED: 'warning',
  TEMP_DISCONNECTED: 'warning',
  TEMPDISCONNECTED: 'warning',
  DISCONNECTED: 'error',
  TERMINATED: 'error',
};

/**
 * The canonical status vocabulary, written down once beside the tone map it is
 * derived from (§A PR-5). One spelling per status — the map above also carries
 * the separator-collapsed variants (`TESTMODE`) so the *tones* survive either
 * wire spelling, but a filter dropdown offering both would read as two
 * different statuses.
 *
 * This exists because the estate's status filter used to offer only the
 * statuses present on the current page — so "show me the AVAILABLE SIMs" was
 * impossible unless one happened to be on screen, which is exactly how
 * activation became undiscoverable. The filter now offers this list unioned
 * with whatever the page observed, so an undocumented new status still
 * appears (§13.8) without the known ones ever disappearing.
 */
export const KNOWN_SIM_STATUSES: readonly string[] = [
  'ACTIVE',
  'AVAILABLE',
  'INITIAL',
  'INACTIVE',
  'TEST_MODE',
  'IN_PROGRESS',
  'SAFE_CUSTODY',
  'SUSPENDED',
  'TEMP_DISCONNECTED',
  'DISCONNECTED',
  'TERMINATED',
];

export function simStatusTone(status: string | null | undefined): BadgeTone {
  return TONE_BY_STATUS[normaliseStatus(status)] ?? 'neutral';
}

/**
 * What the pill says. The vendor's own label, tidied for case only — never
 * translated into a word of ours, because an operator reading a support thread
 * needs the panel and the network to use the same noun.
 */
export function simStatusLabel(status: string | null | undefined): string {
  const raw = (status ?? '').trim();
  if (raw.length === 0) return 'Unknown';
  return raw;
}
